import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { EMPTY, Observable, of, ReplaySubject, shareReplay, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import SafeUrlAssembler from 'safe-url-assembler';
import { ApiEndpointService } from '../../core/services/api-endpoint.service';
import { HttpClientService } from '../../core/services/http/http-client.service';
import { CarrierPipe } from '../../history/pipes/carrier.pipe';
import { Carriers } from '../../shared/enum/general-enum';
import { ICarrierKey } from '../../shared/models/carrier-key.interface';
import { ICountry } from '../../shared/models/country.interface';
import { Location } from '../../shared/models/location/location.model';
import { DateTimeService } from '../../shared/services/date-time/date-time.service';
import { User } from '../../shared/services/user/models/user.model';
import { UtilityService } from '../../shared/services/utility/utility.service';
import {
    IShippingUpdateInformation,
} from '../get-shipping-updates-dialog/models/shipping-update-information.interface';
import { ICarrierLocation } from '../location-search-dialog/models/carrier-location.interface';
import { IAccessPointOriginConfig } from '../models/access-point-origin-config.interface';
import { IAccessPoint } from '../models/access-point.interface';
import { IAddressValidation } from '../models/address-validation.interface';
import { IBillToAccount } from '../models/bill-to-account.interface';
import { ICarrierServiceRequestParam } from '../models/carrier-service-request-param.interface';
import { ICarrierService } from '../models/carrier-service.interface';
import { ICheckRestrictedResponse } from '../models/CheckRestrictedResponse.interface';
import { Commodity } from '../models/commodity.model';
import { Contact } from '../models/contact.model';
import { ICountryRestriction } from '../models/country-restriction.interface';
import { IEmailNotificationTemplate } from '../models/email-notification-template.interface';
import { IHandheldSignature } from '../models/handheld-signature.interface';
import { HarmonizeCommodity } from '../models/harmonize-commodity.model';
import { IPackageType } from '../models/package-type.interface';
import { Package } from '../models/package.model';
import { IShipToZipRestriction } from '../models/ship-to-zip-restriction.interface';
import { IPickupLocation } from '../pickup-locations/models/pickup-location.interface';
import { NotificationType } from 'app/shared/models/notification-type';
import { NotificationService } from 'app/shared/services/notification/notification.service';
import { ErrorHandlerService } from 'app/shared/services/error-handler/error-handler.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root',
})
export class ShipmentService {
    public Quote: Package = new Package();
    public DetailsQuote: Package;
    endPointCall: any;
    public shipToZip$ = new Subject<string>();
    public shipToAddressType$ = new Subject<number>();
    public accessPointSelectorRequested$ = new Subject<boolean>();
    public accessPointSelected$ = new Subject<boolean>();
    public requestShipToAddressValidation$ = new Subject<boolean>();
    public fedexHoldAtLocationRequested$ = new Subject<boolean>();
    public fedexHoldAtLocationSelected$ = new Subject<boolean>();
    public skipDeliveryDefenseFlagChanged$ = new Subject<boolean>();
    public deliveryDefenseCreditDeducted$ = new Subject<boolean>();
    public shippingUpdateInformationChanged$ = new Subject<IShippingUpdateInformation>();
    public shipToZipRestrictionCheckRequested$ = new Subject<boolean>();
    public ddNoScoreAttemptCount = 0;
    private storageName = 'quoteData';
    private dateFormat = 'MM/DD/YYYY';
    private cachedShipmentLocations: { type: string, locations: Location[] }[] = [];
    private cachedShipFromCountries: ICountry[];
    private cachedShipToCountries: ICountry[];

    constructor(private httpClientService: HttpClientService,
                private httpClient: HttpClient,
                private apiEndpointService: ApiEndpointService,
                private carrierPipe: CarrierPipe,
                private dateTimeService: DateTimeService,
                private utilityService: UtilityService,
                private notificationService: NotificationService,
                private errorHandlerService: ErrorHandlerService,
                private translateService: TranslateService
    ) {
    }

    /**
     * Gets a shipment package.
     *
     * @param {string} shipmentId
     * @returns {Observable<IShipmentPackage>}
     */
    public getShipment(shipmentId: string): Observable<Package> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getShipment'))
            .param('shipmentId', shipmentId)
            .toString();
        return this.httpClientService.get<Package>(endpoint);
    }

    public getShipmentLocationDetailsByTrackingId(trackingId: string) {
        const endpoint = SafeUrlAssembler()
          .template(this.apiEndpointService.getEndpoint('getShipmentByTrackingId'))
          .param('trackingId', trackingId)
          .toString();
      
        return this.httpClientService.get<Package>(endpoint).pipe(
          catchError((err) => {
            this.notificationService.notify(
                this.errorHandlerService.getHttpErrorMessage(err),
                this.translateService.instant('dashboard.failedGettingShipmentDetails'),
                NotificationType.ERROR);
            return EMPTY;
          })
        ).subscribe({
          next: (shipmentPackage: Package) => {
            this.Quote.InitialTrackingNumber = trackingId;

                const newFromLocation = new Location();
                newFromLocation.Country = shipmentPackage.ShipTo.Country;
                newFromLocation.CompanyName = shipmentPackage.ShipTo.CompanyName;
                newFromLocation.FirstName = shipmentPackage.ShipTo.FirstName;
                newFromLocation.LastName = shipmentPackage.ShipTo.LastName;
                newFromLocation.StreetAddress = shipmentPackage.ShipTo.StreetAddress;
                newFromLocation.ApartmentSuite = shipmentPackage.ShipTo.ApartmentSuite;
                newFromLocation.City = shipmentPackage.ShipTo.City;
                newFromLocation.State = shipmentPackage.ShipTo.State;
                newFromLocation.Zip = shipmentPackage.ShipTo.Zip;
                newFromLocation.TelNum = shipmentPackage.ShipTo.TelNum;
                newFromLocation.TelephoneNo = shipmentPackage.ShipTo.TelNum;
                newFromLocation.Email = shipmentPackage.ShipTo.Email;
                newFromLocation.ContactId = shipmentPackage.ShipTo.ContactId;
                this.updateFromLocation$.next(newFromLocation);

                const newToLocation = new Location();
                newToLocation.Country = shipmentPackage.ShipFrom.Country;
                newToLocation.CompanyName = shipmentPackage.ShipFrom.CompanyName;
                newToLocation.FirstName = shipmentPackage.ShipFrom.FirstName;
                newToLocation.LastName = shipmentPackage.ShipFrom.LastName;
                newToLocation.StreetAddress = shipmentPackage.ShipFrom.StreetAddress;
                newToLocation.ApartmentSuite = shipmentPackage.ShipFrom.ApartmentSuite;
                newToLocation.City = shipmentPackage.ShipFrom.City;
                newToLocation.State = shipmentPackage.ShipFrom.State;
                newToLocation.Zip = shipmentPackage.ShipFrom.Zip;
                newToLocation.TelNum = shipmentPackage.ShipFrom.TelNum;
                newToLocation.TelephoneNo = shipmentPackage.ShipFrom.TelNum;
                newToLocation.Email = shipmentPackage.ShipFrom.Email;
                newToLocation.ContactId = shipmentPackage.ShipFrom.ContactId;
                this.updateToLocation$.next(newToLocation);
          },
        });
      }

    public updateFromLocation$ = new ReplaySubject<Location>(1);
    public updateToLocation$ = new ReplaySubject<Location>(1);

    public getHandheldSignature(shipmentId: string): Observable<IHandheldSignature> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getHandheldSignature'))
            .query({
                shipmentId, // test tracking number: '784142987420'
                whatImage: true,
            })
            .toString();

        return this.httpClientService.get<IHandheldSignature>(endpoint);
    }

    public getShipmentLabel(shipmentId: string): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getShipmentLabel'))
            .query({
                shipmentId,
            })
            .toString();
        return this.httpClientService.get<any>(endpoint);
    }

    public getCommercialInvoice(shipmentId: string): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getCommercialInvoice'))
            .param('shipmentId', shipmentId)
            .toString();
        return this.httpClient.get(endpoint, { responseType: 'text' });
    }

    public deleteShipment(shipmentId: string): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('deleteShipment'))
            .param('shipmentId', shipmentId)
            .toString();
        return this.httpClientService.delete<any>(endpoint);
    }

    public getShipToCountries(carrier: Carriers | string): Observable<ICountry[]> {
        // if (this.cachedShipToCountries) {
        //     return of(this.cachedShipToCountries);
        // }

        let carrierString: string = carrier as any;
        if (this.utilityService.isValueOfEnum(Carriers, carrier)) {
            carrierString = this.utilityService.getEnumKeyByValue(Carriers, carrier);
        }

        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getCarrierCountries'))
                                           .query({
                                               carriercode: carrierString.toLowerCase(),
                                               isEu: false,
                                               selectionType: 'address', // address || location
                                           })
                                           .toString();

        return this.httpClientService.get<ICountry[]>(endpoint)
                   .pipe(
                       shareReplay(1),
                       map((res) => {
                           // this.cachedShipToCountries = res;
                           return res;
                       }));
    }

    public getShipFromCountries(carrier: Carriers | string): Observable<ICountry[]> {
        // if (this.cachedShipFromCountries) {
        //     return of(this.cachedShipFromCountries);
        // }

        let carrierString: string = carrier as any;
        if (this.utilityService.isValueOfEnum(Carriers, carrier)) {
            carrierString = this.utilityService.getEnumKeyByValue(Carriers, carrier);
        }

        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getCarrierCountries'))
                                           .query({
                                               carriercode: carrierString?.toLowerCase(),
                                               isEu: false,
                                               selectionType: 'location',
                                           })
                                           .toString();
        return this.httpClientService.get<ICountry[]>(endpoint)
                   .pipe(
                       shareReplay(1),
                       map((res) => {
                           // this.cachedShipFromCountries = res;
                           return res;
                       }));
    }

    public getShippingCarrierServices(carrier: Carriers | string,
                                      isDomestic: boolean,
                                      shipFromCountryCode: string,
    ): Observable<ICarrierService[]> {
        let carrierString: string = carrier as any;
        if (this.utilityService.isValueOfEnum(Carriers, carrier)) {
            carrierString = this.utilityService.getEnumKeyByValue(Carriers, carrier);
        }

        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getShippingCarrierServices'))
                                           .query({
                                               isDomestic,
                                               shipFromCountryCode,
                                               carrierCode: carrierString?.toLowerCase(),
                                           })
                                           .toString();

        return this.httpClientService.get<ICarrierService[]>(endpoint);
    }

    public getServicesByCountry(param: ICarrierServiceRequestParam): Observable<ICarrierService[]> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getServicesByCountries'))
                                           .query(param)
                                           .toString();

        return this.httpClientService.get<ICarrierService[]>(endpoint);
    }

    public getShippingCarrierPackageTypes(carrier: Carriers, serviceType: string): Observable<IPackageType> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getShippingCarrierPackageTypes'))
            .query({
                carriercode: carrier,
                carrierservicecode: serviceType,
            })
            .toString();

        return this.httpClientService.get<IPackageType>(endpoint);
    }

    public getQuoteQueryParameters(quote: Package): any {
        const shipmentType = quote.IsHighValueShipment ? 'highvalue' : 'normal';
        return {
            type: shipmentType,
            shipmentid: quote.ShipmentId,
            trackingnumber: quote.TrackingNumber,
            carriercode: this.carrierPipe.transform(quote.CarrierCode).toLowerCase(),
        };
    }

    public getPackageWeight(length: number,
                            width: number,
                            height: number,
                            dimensionUnit: string,
                            user: User,
    ): Observable<number> {
        // const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getPackageWeight'))
        //   .query({
        //     length,
        //     width,
        //     height,
        //   })
        //   .toString();

        // TODO: make a request to the API for the actual calculation.
        // return this.httpClientService.get(endpoint)
        //     .map(res => res.json());

        const divisionFactor = ['inches', 'inch'].includes(dimensionUnit.toLowerCase()) ? 139 : 5000;

        if (this.isEuropeanUser(user)) {
            return of(Math.ceil((length * width * height / divisionFactor) * 2) / 2);
        }

        return of(Math.ceil(length * width * height / divisionFactor));
    }

    public getShipmentLocations(isShipFrom = false, carrierCode): Observable<Location[]> {
        const type = isShipFrom ? 'from' : 'to';
        const cachedLocations = this.cachedShipmentLocations.find(location => location.type === type);
        if (cachedLocations) {
            return of(cachedLocations.locations);
        }

        const endpoint = isShipFrom ?
            SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getLocations')).toString() :
            SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getCarrierAddressBook'))
                              .query({
                                  carriercode: carrierCode,
                              })
                              .toString();

        return this.httpClientService.get<Location[]>(endpoint)
                   .pipe(map((res) => {
                       this.cachedShipmentLocations.push({
                           type,
                           locations: res,
                       });

                       return res;
                   }));
    }

    public getContactAddress(contactId: string): Observable<Location> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getContactAddress'))
                                           .param('contactId', contactId)
                                           .toString();

        return this.httpClientService.get<Location>(endpoint);
    }

    public getCommodities(): Observable<Commodity[]> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getCommodities'))
                                           .toString();

        return this.httpClientService.get<Commodity[]>(endpoint);
    }

    public getCommoditiesByCustomer(): Observable<HarmonizeCommodity[]> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getCommoditiesByCustomer'))
                                           .toString();

        return this.httpClientService.get<HarmonizeCommodity[]>(endpoint);
    }

    public getHarmonizedCommodities(shipFromCountryCode: string): Observable<HarmonizeCommodity[]> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getHarmonizedCommodities'))
                                           .param('countryCode', shipFromCountryCode)
                                           .toString();

        return this.httpClientService.get<HarmonizeCommodity[]>(endpoint);
    }

    public getCarrierKey(carrier: Carriers | string): Observable<ICarrierKey[]> {
        let carrierString: string = carrier as any;
        if (this.utilityService.isValueOfEnum(Carriers, carrier)) {
            carrierString = this.utilityService.getEnumKeyByValue(Carriers, carrier);
        }

        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getCarrierKey'))
                                           .param('carrierCode', carrierString.toLowerCase())
                                           .toString();

        // return of({
        //   AccountNumber: '2E696V',
        //   AccessKey: 'FD0CE62182A02F05',
        // });

        return this.httpClientService.get<ICarrierKey[]>(endpoint);
    }

    public searchCarrierLocations(carrier: Carriers,
                                  phoneNumber: string,
                                  zipCode: string,
                                  distance: string,
    ): Observable<ICarrierLocation> {
        if (+Carriers[carrier] !== +Carriers.Fedex) {
            console.warn('Location search only supports FedEx at the moment.');
            return null;
        }

        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getFedExLocations'))
                                           .query({
                                               distance,
                                               zip: zipCode,
                                               phonenumber: phoneNumber,
                                           })
                                           .toString();

        return this.httpClientService.get<ICarrierLocation>(endpoint);
    }

    public saveLocation(location: Location): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('saveAddressBook'))
            .toString();

        return this.httpClientService.post<any>(endpoint, location);
    }

    public getCustomerBillToAccounts(): Observable<IBillToAccount[]> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getCustomerBillToAccounts'))
                                           .toString();

        return this.httpClientService.get<IBillToAccount[]>(endpoint);
    }

    public checkSchedulePickup(shipFromContactId: string,
                               scheduledDate: Date | string,
                               carrier: Carriers | string,
                               serviceType: string,
    ) {
        let carrierString: string = carrier as any;
        if (this.utilityService.isValueOfEnum(Carriers, carrier)) {
            carrierString = this.utilityService.getEnumKeyByValue(Carriers, carrier);
        }

        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('checkSchedulePickup'))
                                           .query({
                                               shipfromid: shipFromContactId,
                                               scheduleddate: moment(scheduledDate).format('YYYY-MM-DD'),
                                               carriercode: carrierString?.toLowerCase(),
                                               serviceCode: serviceType,
                                           })
                                           .toString();

        return this.httpClientService.get<any>(endpoint);
    }

    public add(newQuote: Package): Observable<Package> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('addQuotes')).toString();

        return this.httpClientService.post<Package>(endpoint, newQuote);
    }

    public saveQuote(quoteId: string): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('saveQuote'))
            .param('quoteId', quoteId)
            .toString();

        return this.httpClientService.put<any>(endpoint, {});
    }

    // public aceCallback(quoteId: string): Observable<any> {
    //     const endpoint = SafeUrlAssembler()
    //         .template(this.apiEndpointService.getEndpoint('AceCallback'))
    //         .param('quoteId', quoteId)
    //         .toString();
    //     return this.httpClientService.get<any>(endpoint);
    // }

    public submitToACE(quoteId: string, status: number): Observable<boolean> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('submitToACE')).toString();
        return this.httpClientService.put<boolean>(endpoint, { 'QuoteId': quoteId, 'Status': status });
    }

    public confirmShipment(quoteId: string): Observable<Package> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('confirmShipment'))
                                           .param('quoteId', quoteId)
                                           .toString();

        return this.httpClientService.post<Package>(endpoint, {});
    }

    public createHighValueShipment(quoteId: string): Observable<any> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('createHighValueShipment'))
                                           .query({
                                               quoteid: quoteId,
                                           })
                                           .toString();

        return this.httpClientService.post<any>(endpoint, {});
    }

    /**
     * [MV3-1410]
     * RULES:
     * 1. If User.Is_International, allow shipping from/to any country.
     * 2. If !User.Is_International && User.Is_Domestic, allow only the following:
     *    US --> [US, PR],
     *    PR --> [US, PR],
     *    XX --> XX (Whatever ship-from country is, we only allow it for ship-to country.)
     * @param {User} user
     * @param {Carriers} carrier
     * @param {string} shipFromCountryCode
     * @returns { availableCountryCodes, forbiddenCountryCodes }
     *    If both are empty arrays, it means no restriction (allow all countries for ship-to).
     *    availableCountryCodes with one empty-string item means no countries are allowed.
     */
    public getRestrictedShipToCountries(user: User, carrier: Carriers, shipFromCountryCode: string): any {
        if (!user || !carrier || !shipFromCountryCode) {
            return;
        }

        switch (+Carriers[carrier]) {
            case Carriers.Fedex:
                return this.createCarrierShipToCountryRestriction(user.IsFedExDomestic, user.IsFedExInternational, shipFromCountryCode);
            case Carriers.Ups:
                return this.createCarrierShipToCountryRestriction(user.IsUPSDomestic, user.IsUPSInternational, shipFromCountryCode);
            case Carriers.Usps:
                return this.createCarrierShipToCountryRestriction(user.IsUSPSDomestic, user.IsUSPSInternational, shipFromCountryCode);
            default:
                console.error('Carrier not yet available');
                return {
                    availableCountryCodes: [],
                    forbiddenCountryCodes: [],
                };
        }
    }

    /**
     * The result (TRUE/FALSE) tells if the specified zip code and country code are in the carrier's do-not-ship list.
     * TRUE means the carrier does not ship; otherwise, FALSE.
     */
    public checkDoNotShip(zipCode: string, carrier: Carriers | string, countryCode: string): Observable<boolean> {
        let carrierString: string = carrier as any;
        if (this.utilityService.isValueOfEnum(Carriers, carrier)) {
            carrierString = this.utilityService.getEnumKeyByValue(Carriers, carrier);
        }

        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('checkDoNotShip'))
                                           .query({
                                               countryCode,
                                               carrierCode: carrierString.toLowerCase(),
                                               zip: zipCode,
                                           })
                                           .toString();

        return this.httpClientService.get<boolean>(endpoint);
    }

    /**
     * Checks if the destination, with the specified insured value, is restricted.
     */
    public checkRestricted(zipCode: string,
                           carrier: Carriers | string,
                           insuredValue: number,
                           serviceType: string,
    ): Observable<ICheckRestrictedResponse> {
        let carrierString: string = carrier as any;
        if (this.utilityService.isValueOfEnum(Carriers, carrier)) {
            carrierString = this.utilityService.getEnumKeyByValue(Carriers, carrier);
        }

        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('checkRestricted'))
                                           .query({
                                               carrier: carrierString?.toLowerCase(),
                                               insuredvalue: insuredValue,
                                               zip: zipCode,
                                               servicecode: serviceType,
                                           })
                                           .toString();

        return this.httpClientService.get<ICheckRestrictedResponse>(endpoint);
    }

    public validateAddress(addressValidation: IAddressValidation, carrier: Carriers): Observable<IAddressValidation> {
        let endpointKey = 'validateAddress';

        if (addressValidation.Country === 'US') {
            switch (carrier) {
                case Carriers.Ups:
                    endpointKey = 'validateAddressUPS';
                    break;
                case Carriers.Fedex:
                    endpointKey = 'validateAddressFedex';
                    break;
                default:
                    endpointKey = 'validateAddress';
                    break;
            }
        }

        const keys = Object.keys(addressValidation);
        for (const key of keys) {
            if (addressValidation[key] === null || addressValidation[key] === undefined) {
                addressValidation[key] = '';
            }
        }

        this.endPointCall = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint(endpointKey)).toString();
        return this.httpClientService.post<IAddressValidation>(this.endPointCall, addressValidation);
    }

    public validateShipToZipRestriction(shipToZipRestriction: IShipToZipRestriction): Observable<IShipToZipRestriction> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('validateShipToZipRestriction'))
            .toString();

        return this.httpClient.post<IShipToZipRestriction>(endpoint, shipToZipRestriction);
    }

    public getPowerOfAttorneyContent(): Observable<string> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getPowerOfAttorneyContent'))
            .toString();
        const options = {
            responseType: 'text',
        };
        return this.httpClient.get(endpoint, { responseType: 'text' });
    }

    public requestHighValueIncrease(obj: Object): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('requestHighValueIncrease'))
            .toString();

        return this.httpClientService.post<any>(endpoint, obj);
    }

    public getRequestServiceOptions(): Observable<any> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('getReturnServices'))
                                           .query({
                                               carrierCode: 'UPS',
                                           })
                                           .toString();

        return this.httpClientService.get<any>(endpoint);
    }

    public restoreQuote() {
        if (!this.Quote) {
            this.Quote = new Package();
        }

        const storedQuote = sessionStorage.getItem(this.storageName);

        if (storedQuote) {
            this.Quote = JSON.parse(storedQuote);
            return;
        }

        this.Quote.ShipTo = new Contact();
        this.Quote.ShipFrom = new Contact();
        this.Quote.Commodities = [];
    }

    public cacheQuote() {
        sessionStorage.setItem(this.storageName, JSON.stringify(this.Quote));
    }

    public clearCache() {
        sessionStorage.removeItem(this.storageName);
        this.cachedShipFromCountries = null;
        this.cachedShipToCountries = null;
        this.cachedShipmentLocations = [];
    }

    public clearQuote() {
        this.Quote = new Package();
    }

    public clearDetailQuote() {
        this.DetailsQuote = null;
    }

    //#region map form data to Quote
    public setCustomerId(customerId: string) {
        this.Quote.ShipTo.CustomerId = customerId;
        this.Quote.ShipFrom.CustomerId = customerId;

        this.cacheQuote();
    }

    public saveShipTo(data) {
        this.Quote.ShipTo.ContactId = data.contactId || 'NOID';
        this.Quote.ShipTo.FirstName = data.firstName;
        this.Quote.ShipTo.LastName = data.lastName;
        this.Quote.ShipTo.CompanyName = data.company;
        this.Quote.ShipTo.Zip = data.zipCode;
        this.Quote.ShipTo.City = data.city;
        this.Quote.ShipTo.State = data.state;
        this.Quote.ShipTo.ProvinceRegion = '';
        this.Quote.ShipTo.Country = data.country;
        this.Quote.ShipTo.StreetAddress = data.address1;
        this.Quote.ShipTo.ApartmentSuite = data.address2;
        this.Quote.ShipTo.AdditionalAddressInformation = data.address3;
        this.Quote.ShipTo.Email = data.email;
        this.Quote.NotifyRecipient = data.emailShipmentNotification;
        this.Quote.NotifyExceptionRecipient = data.emailExceptionNotification;
        this.Quote.NotifyDeliveryRecipient = data.emailDeliveryNotification;
        this.Quote.NotifyCustomShipmentRecipient = data.emailParcelProNotification;
        this.Quote.TemplateID = data.parcelProEmailTemplate;

        // [MV3-2337] Stop Parcel Pro Notify from overriding Package Content
        // if (data.parcelProEmailPackageContent) {
        //   this.Quote.PackageContent = data.parcelProEmailPackageContent;
        // }

        // this.Quote.Photo = data.parcelProEmailPhoto;
        this.Quote.ShipTo.TelephoneNo = data.phone;
        this.Quote.ShipTo.IsResidential = data.residentialCheck;
        this.Quote.ShipToResidential = data.residentialCheck;
        this.Quote.UpdateAddressBook = data.updateAddress;
        this.Quote.AddToExpress = data.addExpress;
        // this.Quote.NotifyRecipient = data.notifyRecipient;

        this.Quote.IsInternational = this.Quote.ShipTo.Country !== this.Quote.ShipFrom.Country;

        this.Quote.ShipToVAT = data.vat || '';
        this.Quote.ShipToTaxID = data.taxId || '';
        this.Quote.ShipToEORI = data.eori || '';

        this.cacheQuote();
    }

    public saveShipToStatic(data) {
        this.Quote.ShipToVAT = data.vat || '';
        this.Quote.ShipToTaxID = data.taxId || '';
        this.Quote.ShipToEORI = data.eori || '';

        this.cacheQuote();
    }

    public saveShipFrom(data) {
        this.Quote.ShipFrom.ContactId = data.contactId || 'NOID';
        this.Quote.ShipFrom.FirstName = data.firstName;
        this.Quote.ShipFrom.LastName = data.lastName;
        this.Quote.ShipFrom.CompanyName = data.company;
        this.Quote.ShipFrom.Zip = data.zipCode;
        this.Quote.ShipFrom.City = data.city;
        this.Quote.ShipFrom.State = data.state;
        this.Quote.ShipFrom.ProvinceRegion = '';
        this.Quote.ShipFrom.Country = data.country;
        this.Quote.ShipFrom.StreetAddress = data.address1;
        this.Quote.ShipFrom.ApartmentSuite = data.address2;
        this.Quote.ShipFrom.AdditionalAddressInformation = data.address3;
        this.Quote.ShipFrom.Email = data.email;
        this.Quote.ShipFrom.TelephoneNo = data.phone;

        if (data.pickupType) {
            this.Quote.UPSPickUpType = data.pickupType;
        }

        this.Quote.IsInternational = this.Quote.ShipTo.Country !== this.Quote.ShipFrom.Country;

        // this.Quote.NotifyRecipient = data.emailShipmentNotification;
        // this.Quote.NotifyExceptionRecipient = data.emailExceptionNotification;
        // this.Quote.NotifyDeliveryRecipient = data.emailDeliveryNotification;
        // this.Quote.NotifyCustomShipmentRecipient = data.emailParcelProNotification;

        this.Quote.ShipFromVAT = data.vat || '';
        this.Quote.ShipFromTaxID = data.taxId || '';
        this.Quote.ShipFromEORI = data.eori || '';

        this.Quote.ShipToResidential = data.residentialCheck !== undefined ? data.residentialCheck : false;
        this.Quote.UpdateAddressBook = data.updateAddress !== undefined ? data.updateAddress : false;
        this.Quote.AddToExpress = data.addExpress !== undefined ? data.addExpress : false;

        this.cacheQuote();
    }

    public saveShipFromStatic(data) {
        this.Quote.NotifySender = data.isNotifySender;
        this.Quote.ShipFromVAT = data.vat || '';
        this.Quote.ShipFromTaxID = data.taxId || '';
        this.Quote.ShipFromEORI = data.eori || '';

        this.cacheQuote();
    }

    public saveShipmentInformation(data) {
        // this.Quote.ShipDate = moment(data.shipDate).startOf('day').toDate();
        this.Quote.ShipDate = this.dateTimeService.toServerDateTimeString(data.shipDate);
        this.Quote.InsuredValue = data.insuredValue;
        this.Quote.CoverageCurrencyCode = data.insuredValueCurrency || 'USD';
        this.Quote.Length = data.packageLength;
        this.Quote.Width = data.packageWidth;
        this.Quote.Height = data.packageHeight;
        this.Quote.Weight = data.weight;
        this.Quote.WeightUOM = data.weightUOM;
        this.Quote.ServiceCode = data.serviceType;
        this.Quote.PackageCode = data.packageType;
        this.Quote.ReferenceNumber = data.referenceNumber;
        this.Quote.ShipmentReason = data.shipmentReasonText;
        this.Quote.CustomField = data.customField;
        this.Quote.CustomFieldValue = data.customFieldValue;

        // [MV3-1474, MV3-1577] HasSED should be set to TRUE if:
        // - insured value and total customs value are more than $2500,
        // - the shipment is AES shipment.
        // [MV3-1647] HasSED should always be TRUE for an ACE shipment.
        // [MV3-1676] Ignore the insured value and add more conditions regarding EEI filing options.
        this.Quote.HasSED = this.setHasSED(); // this.hasHighValueCommodity() && ['1', '2'].includes(this.Quote.EeiFilingOption);

        // TODO: make sure we map correctly.
        this.Quote.CustomerReferenceNumber = data.invoiceNumber;

        this.cacheQuote();
    }

    public savePickupLocation(location: Partial<IPickupLocation>): void {
        if (!location) {
            this.Quote.HoldAtLocationID = null;
            this.Quote.IsHoldAtLocation = false;
            this.Quote.HoldAtAddress1 = '';
            this.Quote.HoldAtAddress2 = '';
            this.Quote.HoldAtCity = '';
            this.Quote.HoldAtState = '';
            this.Quote.HoldAtZip = '';
            this.Quote.HoldAtCountry = '';

            return;
        }

        this.Quote.HoldAtLocationID = location.locationId;
        this.Quote.IsHoldAtLocation = true;
        this.Quote.HoldAtAddress1 = location.address;
        this.Quote.HoldAtAddress2 = location.businessName;
        this.Quote.HoldAtCity = location.city;
        this.Quote.HoldAtState = location.state;
        this.Quote.HoldAtZip = location.zip;
        this.Quote.HoldAtCountry = location.country;

    }

    public saveAdditionalShipmentInformation(data) {
        this.Quote.UseAccessPoint = data.useAccessPoint;
        this.Quote.AccessPoint = data.accessPoint;
        this.Quote.UseAccessPointCode = data.useAccessPointCode;

        this.Quote.IsDirectSignature = data.directChecked || false;
        this.Quote.IsDeliveryConfirmation = data.adultChecked || false;
        this.Quote.IsDDO = data.directDeliveryOnly || false;
        this.Quote.IsSaturdayDelivery = data.saturdayDelivery || false;
        this.Quote.IsSaturdayPickUp = data.saturdayPickup || false;

        this.Quote.IsCod = data.codService || false;
        this.Quote.CodAmount = data.codAmount || 0;
        this.Quote.IsSecuredCod = data.isSecuredCod || false;
        this.Quote.IsBrokerSelect = data.brokerSelect || false;

        this.Quote.BrokerAccountNo = data.brokerAccountNumber;
        this.Quote.BrokerCompanyName = data.brokerCompanyName;
        this.Quote.BrokerContactName = data.brokerContactName;
        this.Quote.BrokerAddress1 = data.brokerAddress1;
        this.Quote.BrokerAddress2 = data.brokerAddress2;
        this.Quote.BrokerPostalCode = data.brokerPostalCode;
        this.Quote.BrokerCity = data.brokerCity;
        this.Quote.BrokerStateProvince = data.brokerStateProvince;
        this.Quote.BrokerCountryCode = data.brokerCountryCode;
        this.Quote.BrokerPhoneNumber = data.brokerPhoneNumber;
        this.Quote.BrokerTaxID = data.brokerTaxId;

        this.cacheQuote();
    }

    public saveShipmentOptions(data) {
        this.Quote.IsThermal = data.thermalPrinter;
        this.Quote.IsReturnShipment = data.returnLabel;
        this.Quote.ReturnLabel = data.returnLabel;

        if (data.returnLabel) {
            this.Quote.ReturnLabelScheduleDate = this.dateTimeService.toServerDateTimeString(data.scheduledReturnDate);
        } else {
            // [MV3-1977] Remove ReturnLabelScheduleDate if ReturnLabel is FALSE.
            // The following line causes:
            //    POST https://api-uat.parcelpro.com/v3.0/quotes 400 (We are experiencing technical difficulties,
            //    we've been notified, please try again at a later time. We apologize for the inconvenience.
            //    Technical Information: SqlDateTime overflow. Must be between 1/1/1753 12:00:00 AM and 12/31/9999 11:59:59 PM.)
            // delete this.Quote.ReturnLabelScheduleDate;

            // The following line causes: {"Code":49,"Message":""}
            // this.Quote.ReturnLabelScheduleDate = null;
        }

        this.mapPickUpType(data.pickupType);

        if (this.Quote.IsPickUpRequested) {
            this.Quote.PickUpDate = this.dateTimeService.toServerDateTimeString(data.pickUpDate);
            this.Quote.PickUpAtHour = data.pickupAtHour;
            this.Quote.PickUpAtMinute = data.pickupAtMinute;
            this.Quote.PickUpByHour = data.pickupByHour;
            this.Quote.PickUpByMinute = data.pickupByMinute;
            this.Quote.PickUpContactName = data.contactName;
            this.Quote.PickUpTelephone = data.phoneNumber;
        } else {
            [
                'PickUpDate',
                'PickUpAtHour',
                'PickUpAtMinute',
                'PickUpByHour',
                'PickUpByMinute',
                'PickUpContactName',
                'PickUpTelephone',
                'PickUpErrorMessage',
            ].forEach(prop => delete this.Quote[prop]);
        }

        this.cacheQuote();
    }

    public mapPickUpType(pickupType: string) {
        this.Quote.IsRegularPickUp = pickupType === '01';
        this.Quote.IsDropoff = pickupType === '02';
        this.Quote.IsPickUpRequested = pickupType === '03';
    }

    public saveShipBillingDetails(data) {
        this.Quote.BillToAccount = data.accountNumber || 'N/A';
        this.Quote.BillToCustomerId = data.billToCustomerId;
        this.Quote.IsBillToThirdParty = data.isBillToThirdParty;
        this.Quote.BillToThirdPartyCountry = data.country;
        this.Quote.BillToThirdPartyPostalCode = data.zipCode;
        this.Quote.BillDutiesTo = data.billDuties;
        this.Quote.BillDutiesToAccount = data.billDutiesAccountNumber;
        this.Quote.BillDutiesToThirdPartyCountry = data.billDutiesCountry;
        this.Quote.BillDutiesToThirdPartyPostalCode = data.billDutiesZipCode;

        this.cacheQuote();
    }

    public saveCommodities(commodities: Commodity[]) {
        this.Quote.Commodities = commodities || [];

        this.cacheQuote();
    }

    public saveCommodityShipInfo(data) {
        this.Quote.PackageContent = 'C';
        this.Quote.ShipmentPurpose = data.shipmentPurpose;
        this.Quote.TotalCustomsValue = data.totalCustomsValue;

        // [MV3-1474] HasSED should be set to TRUE if insured value and total customs value are more than $2500.
        // [MV3-1647] HasSED should always be TRUE for an ACE shipment.
        this.Quote.HasSED = this.setHasSED(); //  this.hasHighValueCommodity() && ['1', '2'].includes(this.Quote.EeiFilingOption);

        this.cacheQuote();
    }

    public resetReturnOption() {
        this.Quote.ReturnServiceCode = '';
        this.Quote.ReturnServiceDescription = '';

        this.cacheQuote();
    }

    public resetImportInformation() {
        this.Quote.CustomsTariffCode = '';

        this.cacheQuote();
    }

    // [MV3-1474, MV3-1577] HasSED should be set to TRUE if:
    // - insured value and total customs value are more than $2500,
    // - the shipment is AES shipment.
    // [MV3-1647] HasSED should always be TRUE for an ACE shipment.
    // [MV3-1676] Ignore the insured value and add more conditions regarding EEI filing options.
    //[MV3-4487][MV3-4787] HasSED should be true - if ShipFrom is US and Ship To is any of these countries(CN, VE, RU, HK)

    public saveReturnServiceInformation(data) {
        this.Quote.ReturnServiceCode = data.ReturnServiceCode;
        this.Quote.ReturnServiceDescription = data.ReturnServiceCodeDesc;

        this.cacheQuote();
    }

    public resetElectronicExportInformation() {
        this.Quote.EeiFilingOption = '';
        delete this.Quote.FilingOption;
        this.Quote.AESCitationType = '';
        this.Quote.AESCitation = '';
        this.Quote.DestinationControlStatement = '';
        this.Quote.MovementReferenceNumber = '';

        this.cacheQuote();
    }

    public saveElectronicExportInformation(data) {
        const isDomestic = !!this.Quote.ShipFrom.Country &&
            !!this.Quote.ShipTo.Country &&
            this.Quote.ShipFrom.Country === this.Quote.ShipTo.Country;

        if (isDomestic) {
            this.resetElectronicExportInformation();
            return;
        }

        this.Quote.EeiFilingOption = data.eeiFilingOption || '0';

        // [MV3-1474] FilingOption should be set to the same value as EeiFilingOption.
        this.Quote.FilingOption = data.eeiFilingOption || '0';

        this.Quote.AESCitationType = data.aesCitationType;
        this.Quote.AESCitation = data.aesCitation;
        this.Quote.DestinationControlStatement = data.destinationControlStatement;
        this.Quote.MovementReferenceNumber = data.mrn ? data.mrn.toUpperCase() : '';

        const isAESShipment = this.Quote.EeiFilingOption === '1';
        this.Quote.ShipFrom.AESCompanyName = isAESShipment ? this.Quote.ShipFrom.CompanyName : null;
        this.Quote.ShipFrom.AESFirstName = isAESShipment ? this.Quote.ShipFrom.FirstName : null;
        this.Quote.ShipFrom.AESLastName = isAESShipment ? this.Quote.ShipFrom.LastName : null;
        this.Quote.ShipTo.AESCompanyName = isAESShipment ? this.Quote.ShipTo.CompanyName : null;
        this.Quote.ShipTo.AESFirstName = isAESShipment ? this.Quote.ShipTo.FirstName : null;
        this.Quote.ShipTo.AESLastName = isAESShipment ? this.Quote.ShipTo.LastName : null;

        this.cacheQuote();
    }

    public saveImportInformation(data) {
        this.Quote.CustomsTariffCode = data.customsTariffCode;

        this.cacheQuote();
    }

    public saveCustomsDocumentation(data) {
        // this.Quote.IsCommercialInvoice = data.isCreateCommercialInvoices;
        this.Quote.IsCommercialInvoice = data.invoiceOption !== null && !isNaN(+data.invoiceOption) && +data.invoiceOption !== 0;
        this.Quote.CommercialInvoiceType = data.invoiceOption;
        this.Quote.CommercialInvoice = data.uploadedInvoice;
        this.Quote.CommercialInvoiceFileType = data.invoiceFileType;
        this.Quote.CommercialInvoiceMimeType = data.invoiceMimeType;

        if (data.invoiceOption === '0' || !data.invoiceOption) {
            delete this.Quote.CommercialInvoiceType;
        }

        if (data.invoiceOption !== '2') {
            delete this.Quote.CommercialInvoice;
        }

        this.Quote.IsNAFTACertificate = data.isNAFTA;

        this.cacheQuote();
        sessionStorage.setItem('shipment-details', JSON.stringify(this.Quote));
    }

    public saveReturnService() {
        this.Quote.IsReturnService = true;
        this.cacheQuote();
    }

    public isDEGBUser(user: User) {
        // return user.UserName.startsWith('DE') || user.UserName.startsWith('GB');
        return ['DE', 'GB'].includes(user?.CountryCode);
    }

    public isDEUser(user: User) {
        // return user.UserName.startsWith('DE');
        return ['DE'].includes(user?.CountryCode);
    }
    public isFRITUser(user: User) {        
        return ['FR', 'IT'].includes(user?.CountryCode);
    }
    public isDEFRITUser(user: User) {        
        return ['DE','FR', 'IT'].includes(user?.CountryCode);
    }
    public isGBUser(user: User) {
        // return user.UserName.startsWith('GB');
        return ['GB'].includes(user?.CountryCode);
    }
    public isEuropeanUser(user: User) {
        return ['DE', 'GB', 'FR', 'IT'].includes(user?.CountryCode);
    }

    //#endregion

    public setCommodities(commodities: Commodity[]) {
        this.Quote.Commodities = commodities;
        this.cacheQuote();
    }

    public hasHighValueCommodity(): boolean {
        return !!this.Quote.Commodities
            && this.Quote.Commodities.reduce(
                (isExceedLimit, commodity) => {
                    return isExceedLimit || +commodity.CustomsValue > 2500 || this.hasAESCommodity(this.Quote.Commodities);
                },
                false);
    }

    // [MV3-2785] Commodity Codes need ITN or AES regardless of value
    public hasAESCommodity(commodities: Commodity[]): boolean {
        if (!commodities || !commodities.length) {
            return false;
        }
        const EEICommodityCodes = ['7102100000', '7102210000', '7102310000'];
        return commodities.some(
            (commodity) => {
                return EEICommodityCodes.includes(commodity.AESHarmonizedCode?.trim());
            },
        );
    }

    public resetQuoteId() {
        this.Quote.QuoteId = 'NOID';

        this.cacheQuote();
    }

    public requestQuote(requesterInfo: any): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('requestQuote'))
            .toString();

        return this.httpClientService.post<any>(endpoint, requesterInfo);
    }

    public getAccessPoints(carrierCode: string, originConfig: IAccessPointOriginConfig): Observable<IAccessPoint[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getAccessPoints'))
            .param('carrierCode', carrierCode.toString().toLowerCase())
            .toString();

        return this.httpClientService.post<IAccessPoint[]>(endpoint, originConfig);
    }

    public getAddressConfidenceLevel(score: number = 0): 'none' | 'low' | 'average' | 'high' {
        if (score === 0) {
            return 'none';
        }

        return score < 500 ? 'low' : (score < 800 ? 'average' : 'high');
    }

    /**
     * Empty array = any country.
     * Array with one empty string = no country.
     *
     * @param {boolean} isDomesticFlag
     * @param {boolean} isInternationalFlag
     * @param {string} shipFromCountryCode
     * @returns {ICountryRestriction}
     */
    private createCarrierShipToCountryRestriction(isDomesticFlag: boolean,
                                                  isInternationalFlag: boolean,
                                                  shipFromCountryCode: string,
    ): ICountryRestriction {
        if (isInternationalFlag) {
            return {
                availableCountryCodes: [],
                forbiddenCountryCodes: [],
            };
        }

        if (isDomesticFlag) {
            return {
                availableCountryCodes: [...this.getAvailableDomesticShipToCountryCodes(shipFromCountryCode)],
                forbiddenCountryCodes: [''],
            };
        }

        return {
            availableCountryCodes: [''],
            forbiddenCountryCodes: [shipFromCountryCode],
        };
    }

    private getAvailableDomesticShipToCountryCodes(shipFromCountryCode: string): string[] {
        switch (shipFromCountryCode) {
            case 'US':
            case 'PR':
                return ['US', 'PR'];
            default:
                return [shipFromCountryCode];
        }
    }

    // MV3-4802 remove HK for AES
    private setHasSED(): boolean {
        return (this.hasHighValueCommodity() && ['1', '2'].includes(this.Quote.EeiFilingOption)) ||
            (this.Quote.ShipFrom.Country === 'US'
                && (this.Quote.ShipTo.Country === 'CN'
                    || this.Quote.ShipTo.Country === 'VE'
                    || this.Quote.ShipTo.Country === 'RU')
            );
    }
}
